import React from 'react';
import { CheckIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { RecentGame } from '../hooks/useRecentGames';

interface RecentGamesQuickSelectProps {
  recentGames: RecentGame[];
  selectedGameId: string;
  onGameSelect: (gameId: string) => void;
  onShowAllGames: () => void;
  loading: boolean;
}

const RecentGamesQuickSelect: React.FC<RecentGamesQuickSelectProps> = ({
  recentGames,
  selectedGameId,
  onGameSelect,
  onShowAllGames,
  loading,
}) => {
  if (loading) {
    return (
      <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide" data-testid="recent-games-loading">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex-shrink-0 w-20 animate-pulse"
          >
            <div className="w-20 h-20 bg-surface-skeleton rounded-lg mb-2" />
            <div className="h-3 bg-surface-skeleton rounded w-16 mx-auto" />
          </div>
        ))}
        <div className="flex-shrink-0 w-20">
          <div className="w-20 h-20 bg-surface-subtle rounded-lg mb-2" />
          <div className="h-3 bg-surface-subtle rounded w-12 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory" data-testid="recent-games-loaded">
      {recentGames.map((recentGame) => (
        <button
          data-track="recent_game_card"
          key={recentGame.game.id}
          type="button"
          onClick={() => onGameSelect(recentGame.game.id)}
          data-testid={`game-option-${recentGame.game.id}`}
          className={`flex-shrink-0 w-20 text-center transition-all snap-start ${
            selectedGameId === recentGame.game.id
              ? 'transform scale-105'
              : 'hover:transform hover:scale-105'
          }`}
        >
          <div
            className={`relative w-20 h-20 rounded-lg overflow-hidden mb-1.5 ${
              selectedGameId === recentGame.game.id
                ? 'ring-2 ring-primary-500 ring-offset-2'
                : 'border border-edge hover:border-primary-300 hover:shadow-md'
            }`}
          >
            {recentGame.game.imageUrl ? (
              <img
                src={recentGame.game.imageUrl}
                alt={recentGame.game.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-surface-skeleton flex items-center justify-center">
                <span className="text-3xl">🎲</span>
              </div>
            )}
            {selectedGameId === recentGame.game.id && (
              <div className="absolute top-1 right-1 bg-primary-500 rounded-full p-0.5 shadow-sm">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
            )}
          </div>
          <p className="text-xs font-medium text-body line-clamp-2 leading-tight px-1">
            {recentGame.game.name}
          </p>
        </button>
      ))}

      {/* "Other..." button */}
      <button
        data-track="recent_game_show_all"
        type="button"
        onClick={onShowAllGames}
        data-testid="game-selection-button-desktop"
        className="flex-shrink-0 w-20 text-center transition-all hover:transform hover:scale-105 snap-start"
      >
        <div className="w-20 h-20 rounded-lg border-2 border-dashed border-edge-input hover:border-primary-400 hover:bg-primary-50 flex items-center justify-center mb-1.5 transition-colors">
          <EllipsisHorizontalIcon className="w-8 h-8 text-disabled" />
        </div>
        <p className="text-xs font-medium text-muted">
          Other...
        </p>
      </button>
    </div>
  );
};

export default RecentGamesQuickSelect;
